import {
  date,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  uuid,
  varchar,
  foreignKey,
  time,
  boolean,
} from 'drizzle-orm/pg-core';
import { workflow_kind } from '@trashlab/api/src/domains/core/schemas/base-template';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { sku_v2 } from '@trashlab/api/src/domains/pricing/schemas/sku-v2';
import { recurrence_settings } from '@trashlab/api/src/domains/orders/schemas/recurrence-settings';
import { subscription_settings } from '@trashlab/api/src/domains/subscription/schemas/subscription-settings';
import { customer_segment } from '@trashlab/api/src/domains/pricing/schemas/customer-segment';
import { PgAddress } from '@trashlab/api/src/domains/core/types';
import { sql } from 'drizzle-orm';

export const customer_order_request_status = pgEnum(
  'customer_order_request_status',
  ['PENDING', 'SUBMITTED', 'ACCEPTED', 'CANCELED'],
);

export const customer_order_request_source = pgEnum(
  'customer_order_request_source',
  ['PORTAL', 'AI'],
);

export const customer_order_request_segment = pgEnum(
  'customer_order_request_segment',
  ['ROLLOFF', 'RESIDENTIAL', 'COMMERCIAL', 'PORTABLE_TOILET'],
);

export const customer_order_request_version = pgEnum(
  'customer_order_request_version',
  ['V1', 'V2'],
);

export const customer_order_request = pgTable(
  'customer_order_request',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    status: customer_order_request_status('status')
      .default('PENDING')
      .notNull(),
    serviceDate: date('serviceDate'),
    preferredTimeStart: time('preferredTimeStart'),
    preferredTimeEnd: time('preferredTimeEnd'),
    customerNotes: varchar('customerNotes', { length: 255 }),
    materialId: uuid('materialId'),
    productId: uuid('productId'),
    workflowKind: workflow_kind('workflowKind'),
    invoiceId: uuid('invoiceId'),
    customerSiteId: uuid('customerSiteId'),
    customerId: uuid('customerId').notNull(),
    number: integer('number').notNull(),
    serviceLineId: uuid('serviceLineId'),
    customerContactId: uuid('customerContactId'),
    source: customer_order_request_source('source').default('PORTAL'),
    // New fields for pricing/selection parity with consumer_cart
    skuId: uuid('skuId'),
    recurrenceSettingsId: uuid('recurrenceSettingsId'),
    subscriptionSettingsId: uuid('subscriptionSettingsId'),
    currentStepKey: varchar('currentStepKey', { length: 255 }),
    requestSegment: customer_order_request_segment('requestSegment'),
    daysOver: integer('daysOver').default(0),
    noContainerRentalNeeded: boolean('noContainerRentalNeeded'),
    // Destination job site for MIGRATE workflow
    destinationJobSiteId: uuid('destinationJobSiteId'),
    // Pickup leg fields (when flow requires)
    pickupServiceDate: date('pickupServiceDate'),
    pickupPreferredTimeStart: time('pickupPreferredTimeStart'),
    pickupPreferredTimeEnd: time('pickupPreferredTimeEnd'),
    // Targeting/analytics
    customerSegmentId: uuid('customerSegmentId'),
    siteAddress: jsonb('siteAddress').$type<PgAddress>(),
    containerRotation: integer('containerRotation'),
    // Versioning for request data shape
    requestVersion: customer_order_request_version('requestVersion')
      .default('V2')
      .notNull(),
  },
  (table) => {
    return {
      site_merge_page_idx: index('order_request_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.id,
      ),
      createdAtTs_idx: index('customer_order_request_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('customer_order_request_tenantId_idx').on(
        table.tenantId,
      ),
      updatedAtTs_idx: index('customer_order_request_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('customer_order_request_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('customer_order_request_versionNumber_idx').on(
        table.versionNumber,
      ),

      customerContactId_idx: index(
        'customer_order_request_customerContactId_idx',
      ).on(table.customerContactId),
      customerId_idx: index('customer_order_request_customerId_idx').on(
        table.customerId,
      ),
      customer_merge_page_idx: index(
        'order_request_customer_merge_page_idx',
      ).on(table.tenantId, table.customerId, table.id),
      customer_number_desc_idx: index(
        'customer_order_request_customer_number_desc_idx',
      ).on(table.customerId, table.number.desc()),
      customerSiteId_idx: index('customer_order_request_customerSiteId_idx').on(
        table.customerSiteId,
      ),
      tenant_destination_site_id_idx: index(
        'customer_order_request_tenant_destination_site_id_idx',
      )
        .on(table.tenantId, table.destinationJobSiteId, table.id)
        .where(sql`${table.destinationJobSiteId} IS NOT NULL`),
      invoiceId_idx: index('customer_order_request_invoiceId_idx').on(
        table.invoiceId,
      ),
      materialId_idx: index('customer_order_request_materialId_idx').on(
        table.materialId,
      ),
      productId_idx: index('customer_order_request_productId_idx').on(
        table.productId,
      ),
      skuId_idx: index('customer_order_request_skuId_idx').on(table.skuId),
      recurrenceSettingsId_idx: index(
        'customer_order_request_recurrenceSettingsId_idx',
      ).on(table.recurrenceSettingsId),
      subscriptionSettingsId_idx: index(
        'customer_order_request_subscriptionSettingsId_idx',
      ).on(table.subscriptionSettingsId),
      customerSegmentId_idx: index(
        'customer_order_request_customerSegmentId_idx',
      ).on(table.customerSegmentId),

      // customer_order_request_customerContactId_fkey: foreignKey({
      //   columns: [table.customerContactId],
      //   foreignColumns: [customer_contact.id],
      //   name: 'customer_order_request_customerContactId_fkey',
      // }),
      // customer_order_request_customerId_fkey: foreignKey({
      //   columns: [table.customerId],
      //   foreignColumns: [customer.id],
      //   name: 'customer_order_request_customerId_fkey',
      // }),
      // customer_order_request_customerSiteId_fkey: foreignKey({
      //   columns: [table.customerSiteId],
      //   foreignColumns: [customerSite.id],
      //   name: 'customer_order_request_customerSiteId_fkey',
      // }),
      // customer_order_request_invoiceId_fkey: foreignKey({
      //   columns: [table.invoiceId],
      //   foreignColumns: [invoice.id],
      //   name: 'customer_order_request_invoiceId_fkey',
      // }),
      // customer_order_request_materialId_fkey: foreignKey({
      //   columns: [table.materialId],
      //   foreignColumns: [material.id],
      //   name: 'customer_order_request_materialId_fkey',
      // }),
      // customer_order_request_productId_fkey: foreignKey({
      //   columns: [table.productId],
      //   foreignColumns: [product.id],
      //   name: 'customer_order_request_productId_fkey',
      // }),
      customer_order_request_skuId_fkey: foreignKey({
        columns: [table.skuId],
        foreignColumns: [sku_v2.id],
        name: 'customer_order_request_skuId_fkey',
      }),
      customer_order_request_recurrenceSettingsId_fkey: foreignKey({
        columns: [table.recurrenceSettingsId],
        foreignColumns: [recurrence_settings.id],
        name: 'customer_order_request_recurrenceSettingsId_fkey',
      }),
      customer_order_request_subscriptionSettingsId_fkey: foreignKey({
        columns: [table.subscriptionSettingsId],
        foreignColumns: [subscription_settings.id],
        name: 'customer_order_request_subscriptionSettingsId_fkey',
      }),
      customer_order_request_customerSegmentId_fkey: foreignKey({
        columns: [table.customerSegmentId],
        foreignColumns: [customer_segment.id],
        name: 'customer_order_request_customerSegmentId_fkey',
      }),
    };
  },
);

export const customer_order_request_history = pgTable(
  'customer_order_request_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    status: customer_order_request_status('status'),
    serviceDate: date('serviceDate'),
    preferredTimeStart: time('preferredTimeStart'),
    preferredTimeEnd: time('preferredTimeEnd'),
    customerNotes: varchar('customerNotes', { length: 255 }),
    materialId: uuid('materialId'),
    productId: uuid('productId'),
    workflowKind: workflow_kind('workflowKind'),
    invoiceId: uuid('invoiceId'),
    customerSiteId: uuid('customerSiteId'),
    customerId: uuid('customerId'),
    number: integer('number'),
    serviceLineId: uuid('serviceLineId'),
    customerContactId: uuid('customerContactId'),
    source: varchar('source', { length: 255 }).default('portal'),
    // New parity/history fields
    skuId: uuid('skuId'),
    recurrenceSettingsId: uuid('recurrenceSettingsId'),
    subscriptionSettingsId: uuid('subscriptionSettingsId'),
    currentStepKey: varchar('currentStepKey', { length: 255 }),
    requestSegment: customer_order_request_segment('requestSegment'),
    daysOver: integer('daysOver'),
    noContainerRentalNeeded: boolean('noContainerRentalNeeded'),
    // Destination job site for MIGRATE workflow
    destinationJobSiteId: uuid('destinationJobSiteId'),
    pickupServiceDate: date('pickupServiceDate'),
    pickupPreferredTimeStart: time('pickupPreferredTimeStart'),
    pickupPreferredTimeEnd: time('pickupPreferredTimeEnd'),
    customerSegmentId: uuid('customerSegmentId'),
    siteAddress: jsonb('siteAddress').$type<PgAddress>(),
    containerRotation: integer('containerRotation'),
    requestVersion: customer_order_request_version('requestVersion'),
  },
);

export const customer_order_request_photo = pgTable(
  'customer_order_request_photo',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    customerOrderRequestId: uuid('customerOrderRequestId').notNull(),
    s3Path: varchar('s3Path').notNull(),
    fileType: varchar('fileType').notNull(),
    notes: text('notes').default('').notNull(),
  },
  (table) => {
    return {
      createdAtTs_idx: index('customer_order_request_photo_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('customer_order_request_photo_tenantId_idx').on(
        table.tenantId,
      ),
      updatedAtTs_idx: index('customer_order_request_photo_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index(
        'customer_order_request_photo_updatedAtTs_idx1',
      ).on(table.updatedAtTs),
      versionNumber_idx: index(
        'customer_order_request_photo_versionNumber_idx',
      ).on(table.versionNumber),

      customerOrderRequestId_idx: index(
        'customer_order_request_photo_customerOrderRequestId_idx',
      ).on(table.customerOrderRequestId),

      customer_order_request_photo_customerOrderRequestId_fkey: foreignKey({
        columns: [table.customerOrderRequestId],
        foreignColumns: [customer_order_request.id],
        name: 'customer_order_request_photo_customerOrderRequestId_fkey',
      }),
    };
  },
);
